import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import { CartProvider } from "./context/CartContext";
import DashboardLayout from "./components/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import LeadsPage from "./pages/LeadsPage";
import OrdersPage from "./pages/OrdersPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import QuotesPage from "./pages/QuotesPage";
import QuoteDetailPage from "./pages/QuoteDetailPage";
import AgentsPage from "./pages/AgentsPage";
import CommissionsPage from "./pages/CommissionsPage";
import ProductsPage from "./pages/ProductsPage";
import SettingsPage from "./pages/SettingsPage";
import PromosPage from "./pages/PromosPage";
import ClientProfilePage from "./pages/ClientProfilePage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import PublicRegistrationPage from "./pages/PublicRegistrationPage";
import PublicAgentRegistrationPage from "./pages/PublicAgentRegistrationPage";
import MaterialeDigitalePage from "./pages/MaterialeDigitalePage";
import SendMePage from "./pages/SendMePage";
import SendMeAdminPage from "./pages/SendMeAdminPage";
import SendMeCampPage from "./pages/SendMeCampPage";
import SendMeCampAdminPage from "./pages/SendMeCampAdminPage";
import FierePage from "./pages/FierePage";
import LeadDetailPage from "./pages/LeadDetailPage";
import EmailTemplatesPage from "./pages/EmailTemplatesPage";
import ClientsMapPage from "./pages/ClientsMapPage";
import BuoniPastoPage from "./pages/BuoniPastoPage";
import "./App.css";

const ProtectedRoute = ({ children, adminOnly = false, sendmeAdminAllowed = false }) => {
  const { user, loading } = useAuth();
  const location = window.location.pathname;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // SendMe Conference Admin can only access sendme-camp-admin page
  if (user.role === "sendme_conference_admin") {
    if (location === "/sendme-camp-admin" || sendmeAdminAllowed) {
      return children;
    }
    return <Navigate to="/sendme-camp-admin" replace />;
  }

  // SendMe Admin can access both sendme pages
  if (user.role === "sendme_admin") {
    if (location === "/sendme-admin" || location === "/sendme-camp-admin" || sendmeAdminAllowed) {
      return children;
    }
    return <Navigate to="/sendme-admin" replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Dynamic redirect component based on user role
const DynamicRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "sendme_conference_admin") {
    return <Navigate to="/sendme-camp-admin" replace />;
  }
  if (user?.role === "sendme_admin") {
    return <Navigate to="/sendme-admin" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  const { user } = useAuth();

  // Determine redirect path based on user role
  const getDefaultPath = () => {
    if (!user) return "/login";
    if (user.role === "sendme_conference_admin") return "/sendme-camp-admin";
    if (user.role === "sendme_admin") return "/sendme-admin";
    return "/dashboard";
  };

  return (
    <Routes>
      <Route path="/register-b2b" element={<PublicRegistrationPage />} />
      <Route path="/register-agent" element={<PublicAgentRegistrationPage />} />
      <Route path="/sendme" element={<SendMePage />} />
      <Route path="/sendmeconference" element={<SendMeCampPage />} />
      <Route path="/fiere" element={<FierePage />} />
      <Route path="/buonipasto" element={<BuoniPastoPage />} />
      <Route
        path="/login"
        element={user ? <Navigate to={getDefaultPath()} replace /> : <LoginPage />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="promos" element={<PromosPage />} />
        <Route index element={<DynamicRedirect />} />
        <Route path="profile" element={<ClientProfilePage />} />
        <Route path="cart" element={<CartPage />} />
        <Route path="checkout" element={<CheckoutPage />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="leads/:id" element={<LeadDetailPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/map" element={<ClientsMapPage />} />
        <Route path="clients/:clientId" element={<ClientDetailPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:orderId" element={<OrderDetailPage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="quotes/:quoteId" element={<QuoteDetailPage />} />
        <Route
          path="agents"
          element={
            <ProtectedRoute adminOnly>
              <AgentsPage />
            </ProtectedRoute>
          }
        />
        <Route path="commissions" element={<CommissionsPage />} />
        <Route
          path="settings"
          element={
            <ProtectedRoute adminOnly>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sendme-admin"
          element={
            <ProtectedRoute adminOnly sendmeAdminAllowed>
              <SendMeAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sendme-camp-admin"
          element={
            <ProtectedRoute adminOnly sendmeAdminAllowed>
              <SendMeCampAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="email-templates"
          element={
            <ProtectedRoute adminOnly>
              <EmailTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route path="materiale-digitale" element={<MaterialeDigitalePage />} />
      </Route>
      <Route path="*" element={<DynamicRedirect />} />
    </Routes>
  );
}

// Wrapper component to access location inside BrowserRouter
function AppContent() {
  const location = useLocation();
  const isSendmePage = location.pathname === "/sendme" || location.pathname === "/sendmeconference" || location.pathname === "/buonipasto";

  return (
    <>
      <AppRoutes />
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          style: {
            fontFamily: "Inter, sans-serif",
          },
        }}
      />
      {/* WhatsApp Support Button - Hidden on /sendme and /sendme-camp pages */}
      {!isSendmePage && (
        <a
          href="https://wa.me/390962937186?text=Ciao%2C%20ho%20bisogno%20di%20supporto%20per%20Lacinio.net"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-5 left-5 z-[9999] flex items-center justify-center w-14 h-14 bg-[#25D366] rounded-full shadow-lg hover:scale-110 transition-transform"
          title="Contattaci su WhatsApp"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      )}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
